import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LifeBuoy, MessageSquare, Pencil, Plus, Trash2 } from 'lucide-react';
import { useDeleteTicket, useTickets, useUsers } from '../hooks/useApi';
import TicketModal from '../components/tickets/TicketModal';
import {
    Avatar,
    Button,
    Card,
    ConfirmDialog,
    DataTable,
    Dropdown,
    EmptyState,
    MenuItem,
    PageHeader,
    Pagination,
    PriorityBadge,
    StatusBadge,
    TableToolbar,
    useSort,
} from '../components/ui';

const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const Tickets = () => {
    const navigate = useNavigate();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketToDelete, setTicketToDelete] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [assigneeFilter, setAssigneeFilter] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);

    const { data: result, isLoading } = useTickets({
        status: statusFilter || undefined,
        search: search || undefined,
        assignee_id: assigneeFilter || undefined,
    });
    const allTickets = useMemo(() => result?.data || [], [result]);

    const { data: usersResult } = useUsers({ limit: 1000 });
    const users = usersResult?.data || [];

    const deleteTicket = useDeleteTicket();
    const { sort, onSort } = useSort({ key: 'created_at', dir: 'desc' });

    const tickets = useMemo(() => {
        const rows = [...allTickets];
        if (sort) {
            const dir = sort.dir === 'asc' ? 1 : -1;
            rows.sort((a, b) => {
                const av = a[sort.key];
                const bv = b[sort.key];
                if (sort.key === 'created_at') {
                    return ((new Date(av || 0).getTime() || 0) - (new Date(bv || 0).getTime() || 0)) * dir;
                }
                return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true }) * dir;
            });
        }
        return rows;
    }, [allTickets, sort]);

    const totalPages = Math.max(1, Math.ceil(tickets.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const pagedTickets = tickets.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const openCreate = () => {
        setSelectedTicket(null);
        setIsCreateModalOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await deleteTicket.mutateAsync(ticketToDelete);
            setTicketToDelete(null);
        } catch (error) {
            console.error('Delete failed', error);
        }
    };

    const hasFilters = Boolean(search || statusFilter || assigneeFilter);

    const columns = [
        {
            key: 'title',
            header: 'Ticket',
            sortable: true,
            render: (t) => (
                <Link to={`/tickets/${t.id}`} className="block group" onClick={(e) => e.stopPropagation()}>
                    <span className="block font-medium text-ink-900 group-hover:text-primary-600 transition-colors duration-150 truncate max-w-[320px]">
                        {t.title}
                    </span>
                    <span className="block text-micro text-ink-500 font-mono mt-0.5">{t.ticket_code}</span>
                </Link>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            sortable: true,
            render: (t) => <StatusBadge status={t.status} />,
        },
        {
            key: 'priority',
            header: 'Priority',
            sortable: true,
            render: (t) => <PriorityBadge priority={t.priority} />,
        },
        {
            key: 'created_by_name',
            header: 'Raised by',
            sortable: true,
            render: (t) => (
                <span className="flex items-center gap-2">
                    <Avatar name={t.created_by_name} size="sm" />
                    <span className="text-ink-700 truncate max-w-[130px]">{t.created_by_name || '—'}</span>
                </span>
            ),
        },
        {
            key: 'assignee_name',
            header: 'Assignee',
            sortable: true,
            render: (t) =>
                t.assignee_name ? (
                    <span className="text-ink-700 truncate block max-w-[130px]">{t.assignee_name}</span>
                ) : (
                    <span className="text-muted">Unassigned</span>
                ),
        },
        {
            key: 'comment_count',
            header: 'Replies',
            align: 'center',
            width: 80,
            render: (t) => (
                <span className="inline-flex items-center gap-1 text-ink-600 tabular">
                    <MessageSquare size={13} className="text-ink-400" aria-hidden="true" />
                    {t.comment_count || 0}
                </span>
            ),
        },
        {
            key: 'created_at',
            header: 'Created',
            sortable: true,
            render: (t) => <span className="text-ink-600 tabular whitespace-nowrap">{formatDate(t.created_at)}</span>,
        },
        {
            key: 'actions',
            header: '',
            align: 'right',
            width: 48,
            render: (t) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <Dropdown label={`Actions for ${t.title}`}>
                        <MenuItem
                            icon={Pencil}
                            onClick={() => {
                                setSelectedTicket(t);
                                setIsCreateModalOpen(true);
                            }}
                        >
                            Edit ticket
                        </MenuItem>
                        <MenuItem icon={Trash2} danger onClick={() => setTicketToDelete(t.id)}>
                            Delete ticket
                        </MenuItem>
                    </Dropdown>
                </div>
            ),
        },
    ];

    return (
        <>
            <PageHeader
                title="Support Tickets"
                description="Requests raised across the organisation, with owner and current state."
                breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Tickets' }]}
                actions={
                    <Button variant="primary" icon={Plus} onClick={openCreate}>
                        New ticket
                    </Button>
                }
            />

            <Card>
                <TableToolbar
                    search={search}
                    onSearchChange={(v) => {
                        setSearch(v);
                        setPage(1);
                    }}
                    searchPlaceholder="Search tickets…"
                    filters={
                        <>
                            <select
                                className="input w-auto"
                                aria-label="Filter by status"
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="">All statuses</option>
                                <option value="open">Open</option>
                                <option value="in_progress">In progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                            </select>

                            <select
                                className="input w-auto"
                                aria-label="Filter by assignee"
                                value={assigneeFilter}
                                onChange={(e) => {
                                    setAssigneeFilter(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="">All assignees</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.first_name} {u.last_name}
                                    </option>
                                ))}
                            </select>
                        </>
                    }
                />

                <DataTable
                    columns={columns}
                    rows={pagedTickets}
                    loading={isLoading}
                    sort={sort}
                    onSort={onSort}
                    onRowClick={(t) => navigate(`/tickets/${t.id}`)}
                    empty={
                        <EmptyState
                            icon={LifeBuoy}
                            title={hasFilters ? 'No tickets match these filters' : 'No tickets yet'}
                            description={
                                hasFilters
                                    ? 'Clear the search or filters to see all tickets.'
                                    : 'Raise a ticket to request support from the ICT team.'
                            }
                            action={
                                hasFilters ? (
                                    <Button
                                        onClick={() => {
                                            setSearch('');
                                            setStatusFilter('');
                                            setAssigneeFilter('');
                                        }}
                                    >
                                        Clear filters
                                    </Button>
                                ) : (
                                    <Button variant="primary" icon={Plus} onClick={openCreate}>
                                        New ticket
                                    </Button>
                                )
                            }
                        />
                    }
                />

                {tickets.length > 0 && (
                    <Pagination
                        page={currentPage}
                        totalPages={totalPages}
                        total={tickets.length}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={(n) => {
                            setPageSize(n);
                            setPage(1);
                        }}
                    />
                )}
            </Card>

            <TicketModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                ticket={selectedTicket}
            />

            <ConfirmDialog
                isOpen={Boolean(ticketToDelete)}
                title="Delete ticket"
                message="This permanently removes the ticket and its replies. This cannot be undone."
                confirmText="Delete ticket"
                variant="danger"
                isLoading={deleteTicket.isPending}
                onConfirm={confirmDelete}
                onClose={() => setTicketToDelete(null)}
            />
        </>
    );
};

export default Tickets;
